// REACT
import { useContext, useState, useEffect } from "react";
import CreatableSelect from "react-select/creatable";
import { toast } from "react-toastify";

// CONTEXT
import { maincontext } from "../../context/maincontext";

// CUSTOM HOOKS
import { useCreate, useRead, useUpdate } from "../../hooks/useFetch";

// ASSETS
import "../../assets/styles/productForm.css";
import { Button, Form } from "react-bootstrap";
import { FaPlus, FaPlusCircle, FaSave, FaEdit } from "react-icons/fa";
import { MdCancel } from "react-icons/md";
import languagePack from "../../assets/languages/languagePack";

// COMPONENTS
import Loading from "../common/Loading";

export default function ProductForm({ setShowModal, isEdit, product, setRefetchTrigger }) {
    const { language, token } = useContext(maincontext);

    // Initialize state based on whether it's an edit or create action
    const [productName, setProductName] = useState(isEdit ? product?.product_name : "");
    const [brandSelectOptions, setBrandSelectOptions] = useState([]);

    const [brandName, setBrandName] = useState(isEdit ? product?.product_group : "");
    const [sku, setSku] = useState(isEdit ? product?.sku : "");

    // URL & Fetch Hooks
    const createUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}product`;
    const updateUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}product/${product?.id}`;
    const brandsUrl = `${process.env.REACT_APP_BACKEND_BASE_URL}product_maingroups`;

    const [dataPackageCreate, setDataPackageCreate] = useState(null);
    const [dataPackageUpdate, setDataPackageUpdate] = useState(null);

    const createProduct = useCreate(dataPackageCreate, createUrl);
    const updateProduct = useUpdate(dataPackageUpdate, updateUrl);
    const brandOptions = useRead(brandsUrl, null);

    useEffect(() => {
        if (!brandOptions.isPending && brandOptions.data) {
            setBrandSelectOptions((brandOptions.data).map(brand => ({
                value: brand,
                label: brand
            })))
        }
    }, [brandOptions.isPending, brandOptions.error, brandOptions.data])

    // Validation Errors
    const [errors, setErrors] = useState({});

    function btnOkHandler() {
        // Validation check
        const newErrors = {};

        if (!productName.trim()) newErrors.productName = languagePack[language].productManager.formFieldValidation1;
        if (!brandName.trim()) newErrors.brandName = languagePack[language].productManager.formFieldValidation2;
        if (!sku.trim()) newErrors.sku = languagePack[language].productManager.formFieldValidation3;

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        // Clear errors & prepare request payload
        setErrors({});

        if (isEdit) {
            setDataPackageUpdate({
                "product_name": productName,
                "product_group": brandName,
                "sku": sku,
            });
        }
        else {
            setDataPackageCreate({
                "product_name": productName,
                "product_group": brandName,
                "sku": sku,
            });
        }
    }

    // Close modal & trigger re-fetch **after API request is complete**
    useEffect(() => {
        if (isEdit) {
            if (updateProduct.error && !updateProduct.isPending) {
                if (updateProduct.error.includes("Product with SKU")) {
                    toast.error(languagePack[language].toast.errorProductEdit, {
                        position: "top-center",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                    });
                }
                else {
                    toast.error(languagePack[language].toast.errorProductEdit, {
                        position: "top-center",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                    });
                }
                console.error("❌ Update Error:", updateProduct.error);
            }
            else if (updateProduct.data && !updateProduct.isPending && !updateProduct.error) {
                toast.success(languagePack[language].toast.successfulProductEdit, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                setShowModal(false);
                setRefetchTrigger(prev => !prev);
            }
        } else {
            if (createProduct.error && !createProduct.isPending) {
                if (createProduct.error.includes("Product with SKU")) {
                    toast.error(languagePack[language].toast.skuExists, {
                        position: "top-center",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                    });
                }
                else {
                    toast.error(languagePack[language].toast.errorProductCreation, {
                        position: "top-center",
                        autoClose: 2000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        progress: undefined,
                        theme: "colored",
                    });
                }

                console.error("❌ Create Error:", createProduct.error);

            }
            else if (createProduct.data && !createProduct.isPending && !createProduct.error) {
                toast.success(languagePack[language].toast.successfulProductCreation, {
                    position: "top-center",
                    autoClose: 2000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "colored",
                });
                setShowModal(false);
                setRefetchTrigger(prev => !prev);
            }
        }

        // eslint-disable-next-line
    }, [createProduct.data, createProduct.isPending, createProduct.error, updateProduct.data, updateProduct.isPending, updateProduct.error]);

    return (
        <div className="productFormModal">
            {token == null || brandOptions.isPending ?
                <Loading /> :
                <div className="productFormModalContent">
                    <div className="d-flex justify-content-center align-items-center gap-4">
                        {isEdit ?
                            <FaEdit className="text-warning " /> :
                            <FaPlusCircle className="text-success" />
                        }
                        <h2>{isEdit ? languagePack[language].productManager.formEditTitle : languagePack[language].productManager.formTitle}</h2>
                    </div>

                    {/* Form Fields */}
                    <Form>
                        <Form.Group className="mb-4 mt-4 formGroup" controlId="productName">
                            <Form.Label className="formLabel">
                                {languagePack[language].productManager.formField1}
                                <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={languagePack[language].productManager.placeholder1}
                                value={productName}
                                onChange={(e) => setProductName(e.target.value)}
                                isInvalid={!!errors.productName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.productName}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group className="mb-4 formGroup" controlId="brandName">
                            <Form.Label className="formLabel">
                                {languagePack[language].productManager.formField2}
                                <span className="required">*</span>
                            </Form.Label>
                            <div className={errors.brandName ? "is-invalid" : ""}>
                                <CreatableSelect
                                    isClearable
                                    isSearchable
                                    options={brandSelectOptions}
                                    value={
                                        brandSelectOptions.find(option => option.value === brandName) ||
                                        (brandName ? { value: brandName, label: brandName } : null)
                                    }
                                    onChange={(selectedOption) =>
                                        setBrandName(selectedOption ? selectedOption.value : "")
                                    }
                                    placeholder={languagePack[language].productManager.placeholder2}
                                    classNamePrefix="react-select"
                                    formatCreateLabel={(inputValue) =>
                                        `${languagePack[language].productManager.createNewOption}: "${inputValue}"`
                                    }
                                />
                                {errors.brandName && (
                                    <div className="invalid-feedback d-block">{errors.brandName}</div>
                                )}
                            </div>
                        </Form.Group>

                        <Form.Group className="mb-4 formGroup" controlId="sku">
                            <Form.Label className="formLabel">
                                {languagePack[language].productManager.formField3}
                                <span className="required">*</span>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={languagePack[language].productManager.placeholder3}
                                value={sku}
                                onChange={(e) => setSku(e.target.value)}
                                isInvalid={!!errors.sku}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.sku}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Form>

                    {/* Buttons Positioned at Bottom */}
                    <div className="modalFooter">
                        <Button variant="secondary" className="productFormModalCancel" onClick={() => setShowModal(false)}>
                            <MdCancel className="create-icon" />&nbsp;{languagePack[language].common.cancel}
                        </Button>

                        <Button variant="primary" className="productFormModalOk" onClick={btnOkHandler} disabled={createProduct.isPending || updateProduct.isPending}>
                            {isEdit ? (
                                <>
                                    {updateProduct.isPending ? <Loading /> : <><FaSave className="create-icon" />&nbsp;{languagePack[language].common.save}</>}
                                </>
                            ) : (
                                <>
                                    {createProduct.isPending ? <Loading /> : <><FaPlus className="create-icon" />&nbsp;{languagePack[language].common.create}</>}
                                </>
                            )}
                        </Button>
                    </div>
                </div>
            }
        </div>
    );
}
